import { useField, useFormikContext } from 'formik';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import { ReactComponent as ChevronRight } from '../../assets/arrow-right-icon.svg';
import InputSearchWithSidebar from '../input-search-with-sidebar/input-search-with-sidebar.component';
import { CountriesResponse } from '../../models/countriesModel';
import { useStore } from '../../stores/store';
import { SidebarName } from '../../types/sidebar.types';

interface Props {
  name: SidebarName;
  placeholder?: string;
  label?: string;
  tabIndex?: number;
  initialCountry?: CountriesResponse;
  onSearchFinished?: () => void;
  onCountrySelected?: (countryCode: string) => void;
}

const CountriesFormFieldSearch = ({
  name,
  placeholder = '',
  label,
  tabIndex = 0,
  initialCountry,
  onSearchFinished,
  onCountrySelected,
}: Props) => {
  const [, meta] = useField(name);
  const { setFieldValue } = useFormikContext();
  const {
    localizationsStore: { availableCountries, getCountryLocalizedTitle },
    userStore,
  } = useStore();
  const [selectedCountry, setSelectedCountry] = useState(initialCountry);

  const { t } = useTranslation();

  useEffect(() => {
    setFieldValue(name, selectedCountry?.country_code, true);
    if (selectedCountry && onCountrySelected) {
      onCountrySelected(selectedCountry.country_code);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, selectedCountry, setFieldValue]);

  return (
    <InputSearchWithSidebar<CountriesResponse>
      name={name}
      placeholder={placeholder}
      label={label}
      sidebarTitle={t('country')}
      sidebarInputPlaceholder={t('start_typing_country')}
      tabIndex={tabIndex}
      inputIcon={ChevronRight}
      displayAllOptionsWithEmptyFilter
      onSearchFinished={() => onSearchFinished && onSearchFinished()}
      getKeyForSearchOption={(country) => country.country_code}
      getDisplayValueForSearchOption={(country) =>
        getCountryLocalizedTitle(country, true)
      }
      errorMessage={meta.error}
      inputValue={getCountryLocalizedTitle(selectedCountry, true)}
      isOptionSelected={(country) =>
        country?.country_code === selectedCountry?.country_code
      }
      onSearchOptionSelected={(country) => {
        setSelectedCountry(country);
      }}
      getSearchOptions={(filter) => {
        const userCountry = userStore.user?.country;
        const countries = filter
          ? availableCountries!.filter((x) =>
              getCountryLocalizedTitle(x, true)
                .toLowerCase()
                .includes(filter.toLocaleLowerCase().trim())
            )
          : availableCountries;
        if (!userCountry) return Promise.resolve(countries);

        const found = countries!.find(
          (country) => userCountry === country.country_code
        );
        if (found) {
          const filteredArray = countries!.filter(
            (country) => country.country_code !== userCountry
          );
          return Promise.resolve([found, ...filteredArray]);
        }
        return Promise.resolve(countries);
      }}
    />
  );
};

export default observer(CountriesFormFieldSearch);
